import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { MapPin, Clock, Building, Eye } from 'lucide-react';
import { useTheme } from '../ui/theme-provider';
import { cn } from '../../lib/utils';
import { getJobTypeColor, formatJobDate, getJobPostedDate } from '../../utils/jobUtils'; // FIXED: Import from jobUtils instead of dateUtils

const JobListView = ({ jobs, loading, onViewJob, onClearFilters }) => {
  const { theme } = useTheme();

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(5)].map((_, i) => (
          <Card key={i} className={cn(
            "animate-pulse",
            theme === 'light' ? 'bg-gray-100' : 'bg-gray-800'
          )}>
            <CardContent className="p-6">
              <div className="space-y-3">
                <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                <div className="h-3 bg-gray-300 rounded w-1/2"></div>
                <div className="h-3 bg-gray-300 rounded w-2/3"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (jobs.length === 0) {
    return (
      <Card className={cn(
        theme === 'light' ? 'bg-white border-gray-200' : 'bg-[#2A2A3E] border-gray-700'
      )}>
        <CardContent className="p-8 text-center">
          <div className="space-y-4">
            <div className={cn(
              "text-4xl mb-4",
              theme === 'light' ? 'text-gray-400' : 'text-gray-600'
            )}>
              🔍
            </div>
            <h3 className={cn(
              "text-lg font-medium",
              theme === 'light' ? 'text-gray-900' : 'text-white'
            )}>
              No jobs found
            </h3>
            <p className={cn(
              "text-sm",
              theme === 'light' ? 'text-gray-600' : 'text-gray-400'
            )}>
              Try adjusting your search criteria or clear all filters to see more results.
            </p>
            <Button onClick={onClearFilters} variant="outline">
              Clear All Filters
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {jobs.map((job) => (
        <Card key={job.id} className={cn(
          "hover:shadow-md transition-shadow cursor-pointer relative", // FIXED: Added 'relative' for absolute positioning context
          theme === 'light' ? 'bg-white border-gray-200 hover:border-gray-300' : 'bg-[#2A2A3E] border-gray-700 hover:border-gray-600'
        )}>
          <CardContent className="p-4 sm:p-6"> {/* Reduced padding on mobile */}
            {/* FIXED: Completely restructured layout - title alone, then location, then badges separately */}
            <div className="flex flex-col h-full">
              {/* Title Row - STANDALONE */}
              <div className="mb-0"> {/* No margin bottom to keep title tight */}
                <h3 className={cn(
                  "font-semibold text-base sm:text-lg leading-tight truncate pr-24", // FIXED: Added pr-24 to prevent overlap with badges
                  theme === 'light' ? 'text-gray-900' : 'text-white'
                )}>
                  {job.title}
                </h3>
              </div>

              {/* Location Row - IMMEDIATELY AFTER TITLE */}
              {job.location && (
                <div className="flex items-center gap-2 text-sm mb-2">
                  <MapPin className={cn(
                    "h-4 w-4 shrink-0",
                    theme === 'light' ? 'text-gray-500' : 'text-gray-400'
                  )} />
                  <span className={cn(
                    "truncate",
                    theme === 'light' ? 'text-gray-700' : 'text-gray-300'
                  )}>
                    {(() => {
                      let locationText = job.location;
                      
                      // FIXED: Mobile-friendly location truncation
                      // If location has comma, show only the part before comma
                      if (locationText.includes(',')) {
                        locationText = locationText.split(',')[0].trim();
                      }
                      
                      // FIXED: Limit characters on mobile to prevent overlap
                      const maxLength = window.innerWidth < 640 ? 20 : 35; // 20 chars on mobile, 35 on desktop
                      if (locationText.length > maxLength) {
                        locationText = locationText.substring(0, maxLength) + '...';
                      }
                      
                      return locationText;
                    })()}
                    {job.distance && (
                      <span className={cn(
                        "ml-2 text-xs font-medium whitespace-nowrap", // FIXED: Added whitespace-nowrap to prevent breaking
                        "text-purple-600 dark:text-purple-400"
                      )}>
                        • {job.distance.toFixed(2)} miles
                      </span>
                    )}
                  </span>
                </div>
              )}

              {/* Badges Row - TOP RIGHT, POSITIONED ABSOLUTELY RELATIVE TO CARD */}
              <div className="absolute top-4 right-4 sm:top-6 sm:right-6 flex flex-col items-end gap-1.5 flex-shrink-0 max-w-[120px] sm:max-w-none"> {/* FIXED: Added max-width constraint on mobile */}
                {/* Employment Type with color dot */}
                {job.employmentType && job.employmentType.trim() && (
                  <div className="flex items-center gap-1.5">
                    <div 
                      className="w-2.5 h-2.5 rounded-full shrink-0"
                      style={{ backgroundColor: getJobTypeColor(job.employmentType) }}
                    />
                    <Badge 
                      variant="outline" 
                      className="text-xs h-5 px-2 truncate max-w-[80px] sm:max-w-none text-white font-medium"
                      style={{ 
                        backgroundColor: getJobTypeColor(job.employmentType),
                        borderColor: getJobTypeColor(job.employmentType)
                      }}
                    >
                      {/* FIXED: Truncate employment type on mobile */}
                      {job.employmentType.length > 8 && window.innerWidth < 640 
                        ? job.employmentType.substring(0, 8) + '...'
                        : job.employmentType
                      }
                    </Badge>
                  </div>
                )}

                {/* Source Badge */}
                <Badge 
                  variant="secondary" 
                  className={cn(
                    "text-xs h-5 px-2 truncate max-w-[80px] sm:max-w-none",
                    // FIXED: Improved contrast for light mode
                    (() => {
                      const isBDJJob = job.source === 'rss' || 
                                       job.source === 'hygienist_therapist' || 
                                       job.source === 'locum_dentist' || 
                                       job.source === 'dental_nurse' ||
                                       (job.originalUrl && job.originalUrl.includes('bdjjobs.com'));
                      return isBDJJob
                        ? theme === 'light' 
                          ? 'bg-blue-600 text-white border-blue-600' // FIXED: Darker blue for better contrast
                          : 'bg-blue-900/30 dark:text-blue-400'
                        : theme === 'light'
                          ? 'bg-green-600 text-white border-green-600' // FIXED: Darker green for better contrast  
                          : 'bg-green-900/30 dark:text-green-400';
                    })()
                  )}
                >
                  {/* FIXED: Restore proper source labels */}
                  {(() => {
                    const isBDJJob = job.source === 'rss' || 
                                     job.source === 'hygienist_therapist' || 
                                     job.source === 'locum_dentist' || 
                                     job.source === 'dental_nurse' ||
                                     (job.originalUrl && job.originalUrl.includes('bdjjobs.com'));
                    
                    if (window.innerWidth < 640) {
                      return isBDJJob ? 'BDJ' : 'Practice';
                    } else {
                      return isBDJJob ? 'BDJ Jobs' : 'Practice';
                    }
                  })()}
                </Badge>
              </div>

              {/* Content: Practice, Role, Salary - with proper spacing */}
              <div className="flex-1 space-y-2 pr-24 sm:pr-20"> {/* FIXED: Increased right padding on mobile */}
                {/* Practice Info */}
                {(job.practiceInfo?.name || job.practiceName) && (
                  <div className="flex items-center gap-2 text-sm">
                    <Building className={cn(
                      "h-4 w-4 shrink-0",
                      theme === 'light' ? 'text-gray-500' : 'text-gray-400'
                    )} />
                    <span className={cn(
                      "truncate font-medium",
                      theme === 'light' ? 'text-purple-700' : 'text-purple-400'
                    )}>
                      {job.practiceInfo?.name || job.practiceName}
                    </span>
                  </div>
                )}

                {/* Role and Salary */}
                <div className="flex items-center gap-3 text-sm flex-wrap">
                  {/* Role Badge */}
                  {job.role && job.role.trim() && (
                    <Badge 
                      variant="outline" 
                      className={cn(
                        "text-xs",
                        // FIXED: Better contrast for role badge
                        theme === 'light'
                          ? 'bg-purple-600 text-white border-purple-600' // Purple background for light mode
                          : 'bg-purple-500 text-white border-purple-500' // Keep purple for dark mode
                      )}
                    >
                      {job.role}
                    </Badge>
                  )}
                  
                  {/* Salary */}
                  {job.salary && job.salary.trim() && job.salary !== 'Competitive' && job.salary !== 'Not specified' && (
                    <span className={cn(
                      "text-sm font-medium",
                      theme === 'light' ? 'text-green-700' : 'text-green-400'
                    )}>
                      {job.salary}
                    </span>
                  )}
                </div>

                {/* Job Description Preview - More compact */}
                {job.description && (
                  <p className={cn(
                    "text-sm line-clamp-2 leading-relaxed",
                    theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                  )}>
                    {job.description.substring(0, 120)}...
                  </p>
                )}
              </div>

              {/* Footer: Posted Date + View Button */}
              <div className="flex items-center justify-between gap-3 mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
                {/* Posted Date */}
                <div className="flex items-center gap-1.5 text-xs">
                  <Clock className={cn(
                    "h-3 w-3",
                    theme === 'light' ? 'text-gray-400' : 'text-gray-500'
                  )} />
                  <span className={cn(
                    theme === 'light' ? 'text-gray-500' : 'text-gray-400'
                  )}>
                    {/* FIXED: Use helper function instead of manual date extraction */}
                    {formatJobDate(getJobPostedDate(job))}
                  </span>
                </div>

                {/* View Button - Bottom aligned */}
                <Button
                  onClick={() => onViewJob(job)}
                  variant="outline"
                  size="sm"
                  className={cn(
                    "h-8 px-4 shrink-0",
                    theme === 'light' 
                      ? 'text-purple-700 border-purple-300 hover:bg-purple-50' 
                      : 'text-purple-300 border-purple-500/50 hover:bg-purple-900/30'
                  )}
                >
                  <Eye className="h-3 w-3 mr-1.5" />
                  View
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default JobListView;
